import {
  fetchApi,
  fetchRest,
  getApiBaseUrl,
  getProformaUrl,
  getStorageFileUrl,
} from "@/config/api";
import type {
  Order,
  OrderFilters as OrderFiltersType,
  OrderDetailResponse,
  Livraison,
  Pays,
  OrderLabel,
  OrderValidations,
  ParcelDimension,
} from "@/types";
import { fetchOrderReplacements } from "./remplacements";

export class OrderConfirmationCancelledError extends Error {
  constructor() { super("Confirmation annulée"); }
}

async function confirmPendingReplacementCancellation(orderId: number, status: number): Promise<boolean> {
  if (![4, 7, 8].includes(status)) return false;
  const proposals = await fetchOrderReplacements(orderId);
  const pending = proposals.some(proposal => Number(proposal.id_commande) === orderId && proposal.statut === 'pending' && proposal.peut_repondre !== false &&
    new Date(proposal.date_expiration).getTime() > Date.now());
  if (!pending) return false;
  if (!window.confirm("Confirmer cette commande annulera ses propositions de remplacement encore en attente. Les articles concernés resteront en rupture.\n\nContinuer la confirmation ?"))
    throw new OrderConfirmationCancelledError();
  return true;
}

// Types pour la génération d'étiquettes
export interface CN23Article {
  brand?: string;
  reference?: string;
  category?: string;
  description: string;
  quantity: number;
  weight: number;
  hsCode: string;
  value: number;
  origin: string;
}

export interface GenerateLabelColissimoParams {
  id_commande: number;
  nbColis: number;
  colisPoids: number[];
  articlesCN23?: CN23Article[][];
}

export interface GenerateLabelDPDParams {
  id_commande: number;
  nbColis: number;
  colisPoids: number[];
  machinable?: boolean;
  instructions?: string;
}

export interface GenerateLabelEasyParams {
  id_commande: number;
  nbColis: number;
  colisPoids: number[];
  parcelDimensions?: ParcelDimension[];
  articles?: CN23Article[][];
  invoiceNumber?: string;
  invoiceDate?: string;
}

export interface ParcelDimensionInput {
  width?: number;
  height?: number;
  length?: number;
}

export interface GenerateLabelUPSParams {
  id_commande: number;
  nbColis: number;
  colisPoids: number[];
  pickupPoint: string;
  dimensions?: ParcelDimensionInput[];
}

export interface GenerateLabelResponse {
  success: boolean;
  etiquettes?: OrderLabel[];
  error?: string;
}

export interface FreeShippingTrackingParcelInput {
  trackingNumber: string;
  carrierId: number;
}

export interface FreeShippingTrackingInput {
  trackingNumber: string;
  carrierId: number;
  parcels?: FreeShippingTrackingParcelInput[];
}

// Récupération de toutes les commandes
export async function ordersList(
  page?: number,
  limit?: number,
  filters?: OrderFiltersType,
): Promise<{ data: Order[]; total: number; page: number; limit: number }> {
  // ✅ Ici on appelle bien la QUERY (et non une mutation)
  const query = `
    query($page: Int, $limit: Int, $filters: CommandeFiltersInput) {
        commandes(page: $page, limit: $limit, filters: $filters) {
            data {
                id_commande
                id_commande_name
                id_commande_groupe
                montantTotal
                montantApresRemise
                montantRuptureHT
                montantCA
                acheteur {
                    nomSociete
                }
                isPremiereCommande
                nb_colis
                statut
                id_livraison
                dateCreation
                dateCommande
                dateExpedition
                commandeStatut {
                    id_statut
                    statut_fr
                }
                livraison {
                    libelle
                }
                commandeGroupe {
                    id_commande_groupe
                    groupage
                    date_creation
                    date_expedition
                }
            }
            total
            page
            limit
        }
    }
  `;
  // Nettoyer les filtres : supprimer les valeurs vides pour éviter les problèmes avec le backend
  const cleanedFilters = filters
    ? Object.entries(filters).reduce((acc, [key, value]) => {
        if (value !== null && value !== undefined && value !== "") {
          acc[key] = value;
        }
        return acc;
      }, {} as any)
    : undefined;

  const variables = { page, limit, filters: cleanedFilters };

  const response = await fetchApi<{
    commandes: { data: Order[]; total: number; page: number; limit: number };
  }>(query, variables);

  // ✅ On renvoie uniquement le tableau de commandes
  return response.commandes;
}

export async function updateStatus(
  orderId: number,
  status: number,
): Promise<Order> {
  const cancelPending = await confirmPendingReplacementCancellation(orderId, status);
  const mutation = `
    mutation {
      updateOrderStatus(id_commande: ${orderId}, statut: ${parseInt(
    status.toString(),
  )}, annuler_remplacements_en_attente: ${cancelPending}) {
        id_commande
        statut
      }
    }
  `;

  const response = await fetchApi<{ updateOrderStatus: Order }>(mutation);

  return response.updateOrderStatus;
}

export async function addComment(): Promise<Order> {
  // TODO: Implémenter l'ajout de commentaire
  throw new Error("Not implemented");
  // const mutation = `
  //   mutation AddComment($id: Int!, $comment: String!) {
  //     addOrderComment(id_commande: $id, comment: $comment) {
  //       id_commande
  //       commentaires
  //     }
  //   }
  // `;
  // const variables = { id: orderId, comment };
  // const response = await fetchApi<{ addOrderComment: Order }>(mutation, variables);
  // return response.addOrderComment;

  // Placeholder
  throw new Error("Not implemented yet");
}

/**
 * Récupère tous les transporteurs (livraisons) depuis la base de données
 */
export async function getLivraisons(): Promise<Livraison[]> {
  const query = `
    query {
      livraisons {
        id_livraison
        libelle
        telephone
        fax
        email
        site_web
        commentaires
        transporteur
        active
        supprimer
      }
    }
  `;

  const response = await fetchApi<{ livraisons: Livraison[] }>(query);

  // Filtrer uniquement les transporteurs actifs et non supprimés
  return response.livraisons.filter(
    (livraison) => livraison.active === 1 && livraison.supprimer === "0",
  );
}

/**
 * Récupère les pays disponibles pour les commandes d'un vendeur
 * (basé sur les pays des commandes existantes)
 */
export async function getCountriesFromOrders(): Promise<Pays[]> {
  // Récupère les pays des acheteurs qui ont commandé chez le vendeur connecté
  // Reproduit la logique de l'ancien système PHP
  const query = `
    query {
      countriesFromOrders {
        id_pays
        texte_fr
        texte_uk
        code
      }
    }
  `;

  const response = await fetchApi<{ countriesFromOrders: Pays[] }>(query);

  // Trier par nom français
  return response.countriesFromOrders.sort((a, b) =>
    a.texte_fr.localeCompare(b.texte_fr),
  );
}

/**
 * Récupère les détails complets d'une commande par son ID
 */
export async function getOrderById(
  orderId: number,
): Promise<OrderDetailResponse | null> {
  const query = `
    query GetOrderDetail($id: Int!) {
      commandeById(id: $id) {
        id_commande
        id_commande_name
        id_commande_groupe
        montantTotal
        montantApresRemise
        montantCA
        poids
        nb_colis
        statut
        id_livraison
        Commentaires
        data
        id_adresse_livraison
        id_adresse_facturation
        
        # Informations client
        acheteur {
          id_acheteur
          nomContact
          prenomContact
          email
          nomSociete
          tva_intra
          eori
          active_tva
          data
        }
        
        # Transporteur
        livraison {
          id_livraison
          id_acheteur
          libelle
          commentaires
          telephone
          fax
          email
          site_web
          id_client_transporteur
        }
        
        # Mode de paiement
        paiement {
          id_paiement
          texte_fr
        }
        
        # Commande groupe (contient groupage et autres infos)
        commandeGroupe {
          id_commande_groupe
          id_commande_groupe_name
          groupage
          data
          date_creation
          frais_port_src
          frais_port
        }

        groupageReadiness {
          countReady
          countTotal
        }

        parcelScanInfo {
          expectedCount
          scannedCount
          scannedTrackingNumbers {
            tracking
            parcelNumber
            carrierTrackingNumber
            carrierId
            scannedAt
          }
        }

        pickupColisInfo {
          id_colis_pickup
          nb_colis
          nom_reception
          date_creation
        }
        
        # Dates de la commande
        dateCommande
        dateCreation
        dateExpedition
        
        # Adresse de livraison
        adresseLivraison {
          id_adresse
          adresse
          codePostal
          ville
          telephone
          mobile
          NomContact
          Societe
          pays {
            id_pays
            code
            texte_fr
            is_cee
          }
        }
        
        # Adresse de facturation
        adresseFacturation {
          id_adresse
          adresse
          codePostal
          ville
          telephone
          mobile
          NomContact
          Societe
          pays {
            id_pays
            code
            texte_fr
            is_cee
          }
        }
        
        # Lignes de commande (produits)
        lignes {
          id_ligne
          id_commande
          id_produit
          id_couleur
          quantite_pack
          corbeille
          quantites {
            q1
            q2
            q3
            q4
            q5
            q6
            q7
            q8
            q9
            q10
            q11
            q12
          }
          prixLigne
          reference
          reference_base
          prix
          prixReduit
          poids_produit
          poids
          vendu_par
          id_vendeur
          couleur_FR
          couleur_EN
          couleur_UK
          couleur_hex
          couleur_officiel
          declinaisons {
            d1_FR
            d2_FR
            d3_FR
            d4_FR
            d5_FR
            d6_FR
            d7_FR
            d8_FR
            d9_FR
            d10_FR
            d11_FR
            d12_FR
            d1_UK
            d2_UK
            d3_UK
            d4_UK
            d5_UK
            d6_UK
            d7_UK
            d8_UK
            d9_UK
            d10_UK
            d11_UK
            d12_UK
          }
          quantite_total
          imageUrl
          categorie
          categorie_en
          id_categorie
          provenance_code
        }
        remises {
          id_commande_remise
          id_commande
          id_commande_groupe
          id_remise
          libelle
          typeRemise
          auteur
          type
          montant
          isFraisPort
        }
        optionClient
        outsideEuShippingZone
        needsCustomsDeclaration
        requiresUsCustomsCompliance
        usCustomsNoticeAccepted
      }
    }
  `;

  const variables = { id: orderId };
  const response = await fetchApi<{ commandeById: OrderDetailResponse | null }>(
    query,
    variables,
  );

  // Si la commande n'existe pas, le backend lance une NotFoundException
  // qui est transformée en erreur GraphQL, donc on ne devrait jamais avoir null ici
  // Mais on le gère quand même pour être sûr
  return response.commandeById || null;
}

/**
 * Met des produits en rupture pour une commande
 */
export async function actionRupture(
  id_commande: number,
  idLignes: number[],
): Promise<boolean> {
  const mutation = `
    mutation ActionRupture($input: ActionRuptureInput!) {
      actionRupture(input: $input)
    }
  `;

  const variables = {
    input: {
      id_commande,
      idLignes,
    },
  };

  const response = await fetchApi<{ actionRupture: boolean }>(
    mutation,
    variables,
  );

  return response.actionRupture || false;
}

/**
 * Réactive des produits en rupture pour une commande
 */
export async function actionReactive(
  id_commande: number,
  idLignes: number[],
): Promise<boolean> {
  const mutation = `
    mutation ActionReactive($input: ActionReactiveInput!) {
      actionReactive(input: $input)
    }
  `;

  const variables = {
    input: {
      id_commande,
      idLignes,
    },
  };

  const response = await fetchApi<{ actionReactive: boolean }>(
    mutation,
    variables,
  );

  return response.actionReactive || false;
}

/**
 * Divise une ou plusieurs lignes de commande
 */
export async function actionDiviser(
  id_commande: number,
  lignes: Array<{
    id_ligne: number;
    base_qt: number[];
    qt: number[];
    base_prixligne: number;
    prixligne: number;
  }>,
): Promise<boolean> {
  const mutation = `
    mutation ActionDiviser($input: ActionDiviserInput!) {
      actionDiviser(input: $input)
    }
  `;

  const variables = {
    input: {
      id_commande,
      lignes,
    },
  };

  const response = await fetchApi<{ actionDiviser: boolean }>(
    mutation,
    variables,
  );

  return response.actionDiviser || false;
}

// Interface pour les étiquettes
/**
 * Récupère toutes les étiquettes d'une commande
 * Le champ 'etiquette' contient l'URL (chemin relatif ou URL complète)
 */
export async function getOrderLabels(orderId: number): Promise<OrderLabel[]> {
  const query = `
    query GetOrderLabels($id: Int!) {
      commandeEtiquettes(idCommande: $id) {
        id_commande_etiquette
        id_commande
        tracking_number
        etiquette
        id_livraison
      }
    }
  `;

  const variables = { id: orderId };
  const response = await fetchApi<{ commandeEtiquettes: OrderLabel[] }>(
    query,
    variables,
  );

  const labels = response.commandeEtiquettes || [];
  // URL de base pour les fichiers statiques (à ajuster selon votre configuration)
  const baseUrl = process.env.VUE_APP_API_BASE_URL;

  return labels.map((label) => {
    // Construire l'URL complète
    let url = label.etiquette;
    if (!url.startsWith("http://") && !url.startsWith("https://")) {
      // Si c'est un chemin relatif, préfixer avec l'URL de base
      url = `${baseUrl}/${label.etiquette}`;
    }

    return {
      ...label,
      url,
    };
  });
}

/** Télécharge toutes les étiquettes d'une commande dans un PDF fusionné. */
export async function downloadAllOrderLabels(orderId: number): Promise<Blob> {
  const baseUrl = getApiBaseUrl().replace(/\/$/, "");
  const response = await fetchRest(
    `${baseUrl}/api/order-labels/${orderId}/download-all`,
  );
  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || "Impossible de télécharger les étiquettes");
  }
  return response.blob();
}

// // Update order status
// export async function updateStatus(orderId: number, status: string): Promise<Order> {
// Mettre à jour le statut d'une commande
export async function updateOrderStatus(
  orderId: number,
  status: number,
  reasonCode?: string,
  reasonDetail?: string,
  freeShippingTracking?: FreeShippingTrackingInput,
): Promise<OrderDetailResponse | null> {
  const cancelPending = await confirmPendingReplacementCancellation(orderId, status);
  const mutation = `
    mutation($id_commande: Int!, $statut: Int!, $reasonCode: String, $reasonDetail: String, $freeShippingTracking: FreeShippingTrackingInput, $cancelPending: Boolean) {
      updateOrderStatus(
        id_commande: $id_commande
        statut: $statut
        raison_annulation_code: $reasonCode
        raison_annulation_detail: $reasonDetail
        suivi_livraison_gratuite: $freeShippingTracking
        annuler_remplacements_en_attente: $cancelPending
      ) {
        id_commande
        statut
      }
    }
  `;

  await fetchApi<{
    updateOrderStatus: { id_commande: number; statut: number };
  }>(mutation, {
    id_commande: orderId,
    statut: status,
    reasonCode,
    reasonDetail,
    freeShippingTracking,
    cancelPending,
  });

  // Recharger les données complètes de la commande après la mise à jour
  return await getOrderById(orderId);
}

// Récupérer toutes les validations pour une commande
export async function getOrderValidations(
  orderId: number,
): Promise<OrderValidations> {
  const query = `
    query($id_commande: Int!) {
      orderValidations(id_commande: $id_commande) {
        disableAction
        hasUploadedInvoices
        hasColisDimensions
        activeExpedi
      }
    }
  `;

  const variables = { id_commande: orderId };
  const response = await fetchApi<{ orderValidations: OrderValidations }>(
    query,
    variables,
  );

  return response.orderValidations;
}

// Sauvegarder les dimensions des colis pour une commande
export async function saveParcelDimensions(
  orderId: number,
  colisData: ParcelDimension[],
): Promise<void> {
  const mutation = `
    mutation($id_commande: Int!, $coliinfo: String!) {
      saveParcelDimensions(id_commande: $id_commande, coliinfo: $coliinfo)
    }
  `;

  const variables = {
    id_commande: orderId,
    coliinfo: JSON.stringify(colisData),
  };

  await fetchApi<{ saveParcelDimensions: boolean }>(mutation, variables);
}

export async function removeParcelDimension(
  orderId: number,
  parcelIndex: number,
): Promise<void> {
  const mutation = `
    mutation($id_commande: Int!, $parcel_index: Int!) {
      removeParcelDimension(
        id_commande: $id_commande
        parcel_index: $parcel_index
      )
    }
  `;

  await fetchApi<{ removeParcelDimension: boolean }>(mutation, {
    id_commande: orderId,
    parcel_index: parcelIndex,
  });
}

export async function acknowledgeUsCustomsNotice(
  orderId: number,
): Promise<boolean> {
  const mutation = `
    mutation AcknowledgeUsCustomsNotice($id_commande: Int!) {
      acknowledgeUsCustomsNotice(id_commande: $id_commande)
    }
  `;
  const response = await fetchApi<{ acknowledgeUsCustomsNotice: boolean }>(
    mutation,
    { id_commande: orderId },
  );
  return response.acknowledgeUsCustomsNotice;
}

// Sauvegarder les dimensions depuis une session administrateur en impersonation
export async function adminSaveParcelDimensions(
  orderId: number,
  colisData: ParcelDimension[],
): Promise<void> {
  const mutation = `
    mutation($id_commande: Int!, $coliinfo: String!) {
      adminSaveParcelDimensions(id_commande: $id_commande, coliinfo: $coliinfo)
    }
  `;
  const variables = {
    id_commande: orderId,
    coliinfo: JSON.stringify(colisData),
  };

  try {
    await fetchApi<{ adminSaveParcelDimensions: boolean }>(mutation, variables);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    if (!message.includes('Cannot query field "adminSaveParcelDimensions"')) {
      throw error;
    }
    await saveParcelDimensions(orderId, colisData);
  }
}

// Uploader une facture pour une commande groupée
export async function uploadInvoice(formData: FormData): Promise<{
  success: boolean;
  filename: string;
  url: string;
  path: string;
  size: number;
}> {
  const apiBaseUrl = process.env.VUE_APP_API_BASE_URL;
  const response = await fetchRest(`${apiBaseUrl}/api/upload-invoice`, {
    method: "POST",
    body: formData,
  });

  if (!response.ok) {
    let errorMessage = "Erreur lors de l'upload de la facture";

    try {
      // Essayer de parser la réponse comme JSON (NestJS retourne souvent du JSON)
      const errorData = await response.json();
      if (errorData.message) {
        errorMessage = errorData.message;
      } else if (typeof errorData === "string") {
        errorMessage = errorData;
      }
    } catch {
      // Si ce n'est pas du JSON, lire comme texte
      const errorText = await response.text();
      if (errorText) {
        errorMessage = errorText;
      }
    }

    // Messages spécifiques selon le code HTTP
    if (response.status === 413 || response.status === 400) {
      // 413 = Payload Too Large, 400 = Bad Request (peut être taille ou format)
      if (
        errorMessage.includes("fileSize") ||
        errorMessage.includes("too large") ||
        errorMessage.includes("volumineux")
      ) {
        errorMessage =
          "Fichier trop volumineux. La taille maximale autorisée est de 10 MB.";
      }
    }

    throw new Error(errorMessage);
  }

  return await response.json();
}

// Ouvrir la pré-commande PDF dans un nouvel onglet
export async function downloadPrecommande(orderId: number): Promise<void> {
  const apiBaseUrl = process.env.VUE_APP_API_BASE_URL;
  // Le backend génère le PDF, l'upload sur S3 et renvoie le chemin relatif.
  const response = await fetchRest(
    `${apiBaseUrl}/api/precommande/${orderId}`,
    { method: "GET" },
  );

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ message: "Erreur lors de la génération de la pré-commande" }));
    throw new Error(error.message || "Erreur lors de la génération de la pré-commande");
  }

  const data = await response.json();
  // Ouvrir directement depuis le storage (S3) et non via localhost.
  window.open(getProformaUrl(data.url), "_blank");
}

/** Justificatif d'expédition destiné à l'acheteur (preuve de suivi). */
export async function downloadJustificatifExpedition(
  orderId: number,
): Promise<void> {
  const cacheBust = Date.now();
  const response = await fetchRest(
    `${getApiBaseUrl()}/api/justificatif-expedition/${orderId}?v=${cacheBust}`,
    { method: "GET", cache: "no-store" },
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({
      message: "Erreur lors de la génération du justificatif d'expédition",
    }));
    throw new Error(
      error.message ||
        "Erreur lors de la génération du justificatif d'expédition",
    );
  }

  const data = await response.json();
  if (!data.url) throw new Error("URL storage absente");
  const storageUrl = getStorageFileUrl(data.url);
  const separator = storageUrl.includes("?") ? "&" : "?";
  window.open(`${storageUrl}${separator}v=${cacheBust}`, "_blank");
}

export async function downloadSupplierInvoice(orderId: number): Promise<void> {
  const response = await fetchRest(
    `${getApiBaseUrl()}/api/admin-efp/commandes/${orderId}/facture-grossiste`,
    { method: "GET" },
  );
  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ message: "Impossible de récupérer la facture" }));
    throw new Error(error.message || "Impossible de récupérer la facture");
  }

  const data = (await response.json()) as {
    exists: boolean;
    url: string | null;
  };
  if (!data.exists || !data.url) {
    throw new Error("Aucune facture grossiste n'est disponible pour cette commande");
  }
  window.open(
    data.url.startsWith("http")
      ? data.url
      : `${getApiBaseUrl()}${data.url}`,
    "_blank",
  );
}

export interface ShippingDocument {
  exists: boolean;
  filename: string;
  url: string | null;
}

export async function getShippingDocument(
  orderId: number,
): Promise<ShippingDocument> {
  const response = await fetchRest(
    `${getApiBaseUrl()}/api/shipping-document/${orderId}`,
    { method: "GET" },
  );
  if (!response.ok) {
    throw new Error("Impossible de vérifier le document commercial");
  }
  return await response.json();
}

export async function uploadShippingDocument(
  orderId: number,
  file: File,
): Promise<ShippingDocument> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("idCommande", String(orderId));
  const response = await fetchRest(
    `${getApiBaseUrl()}/api/shipping-document`,
    { method: "POST", body: formData },
  );
  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    throw new Error(
      payload?.message || "Erreur lors de l'upload du document commercial",
    );
  }
  return await response.json();
}

export async function deleteShippingDocument(
  orderId: number,
): Promise<void> {
  const response = await fetchRest(
    `${getApiBaseUrl()}/api/shipping-document/${orderId}`,
    { method: "DELETE" },
  );
  if (!response.ok) {
    throw new Error("Erreur lors de la suppression du document commercial");
  }
}

// Récupérer la liste des factures uploadées pour une commande
export async function getUploadedInvoices(
  orderId: number,
): Promise<Array<{ filename: string; url: string }>> {
  const apiBaseUrl = getApiBaseUrl();
  const response = await fetchRest(`${apiBaseUrl}/api/invoices/${orderId}`, {
    method: "GET",
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Erreur HTTP: ${response.status} - ${errorText}`);
  }

  const invoices = await response.json();
  // Construire les URLs complètes : le backend sert S3 puis le fallback v1.
  return invoices.map((inv: { filename: string; url: string }) => ({
    filename: inv.filename,
    url: inv.url.startsWith("http") ? inv.url : `${apiBaseUrl}${inv.url}`,
  }));
}

// Supprimer une facture uploadée
export async function deleteInvoice(
  orderId: number,
  filename: string,
): Promise<{ success: boolean }> {
  const apiBaseUrl = process.env.VUE_APP_API_BASE_URL;
  // Encoder le nom de fichier pour l'URL
  const encodedFilename = encodeURIComponent(filename);
  const response = await fetchRest(
    `${apiBaseUrl}/api/invoices/${orderId}/${encodedFilename}`,
    {
      method: "DELETE",
    },
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Erreur HTTP: ${response.status} - ${errorText}`);
  }

  return await response.json();
}

/**
 * Génère le PDF des détails de colis pour une commande (groupage ou OCX).
 * Si `colis` est fourni, les dimensions sont utilisées directement (évite un
 * décalage avec la base juste après l'enregistrement).
 */
export async function generatePackageDetailsPDF(
  orderId: number,
  colis?: ParcelDimension[],
): Promise<{ generateUrl: string }> {
  const apiBaseUrl =
    process.env.VUE_APP_API_BASE_URL || "http://localhost:3002";

  const hasColis = Array.isArray(colis) && colis.length > 0;
  const response = await fetchRest(
    `${apiBaseUrl}/api/package-details/${orderId}/generate`,
    {
      method: hasColis ? "POST" : "GET",
      headers: {
        "Content-Type": "application/json",
      },
      body: hasColis ? JSON.stringify({ colis }) : undefined,
    },
  );

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ message: "Erreur lors de la génération du PDF" }));
    throw new Error(error.message || "Erreur lors de la génération du PDF");
  }

  const data = await response.json();
  if (!data.generateUrl) throw new Error("URL storage absente");
  const storageUrl = getStorageFileUrl(data.generateUrl);
  const cacheBuster = `_=${Date.now()}`;
  const generateUrl = storageUrl.includes("?")
    ? `${storageUrl}&${cacheBuster}`
    : `${storageUrl}?${cacheBuster}`;
  return {
    ...data,
    generateUrl,
  };
}

/**
 * Télécharge le PDF des détails de colis pour une commande
 */
export async function downloadPackageDetailsPDF(
  orderId: number,
): Promise<void> {
  const apiBaseUrl =
    process.env.VUE_APP_API_BASE_URL || "http://localhost:3002";

  const response = await fetchRest(`${apiBaseUrl}/api/package-details/${orderId}`, {
    method: "GET",
  });

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ message: "Erreur lors du téléchargement du PDF" }));
    throw new Error(error.message || "Erreur lors du téléchargement du PDF");
  }

  const data = await response.json();
  if (!data.url) throw new Error("URL storage absente");
  window.open(getStorageFileUrl(data.url), "_blank");
}

/**
 * Génère les étiquettes Colissimo
 */
export async function generateLabelColissimo(
  params: GenerateLabelColissimoParams,
): Promise<GenerateLabelResponse> {
  const mutation = `
    mutation GenerateLabelColissimo($input: GenerateLabelColissimoInput!) {
      generateLabelColissimo(input: $input) {
        success
        error
        etiquettes {
          id_commande_etiquette
          id_commande
          id_commande_name
          id_livraison
          etiquette
          tracking_number
        }
      }
    }
  `;

  const variables = { input: params };
  const response = await fetchApi<{
    generateLabelColissimo: GenerateLabelResponse;
  }>(mutation, variables);
  return response.generateLabelColissimo;
}

/**
 * Génère les étiquettes DPD
 */
export async function generateLabelDPD(
  params: GenerateLabelDPDParams,
): Promise<GenerateLabelResponse> {
  const mutation = `
    mutation GenerateLabelDPD($input: GenerateLabelDPDInput!) {
      generateLabelDPD(input: $input) {
        success
        error
        etiquettes {
          id_commande_etiquette
          id_commande
          id_commande_name
          id_livraison
          etiquette
          tracking_number
        }
      }
    }
  `;

  const variables = { input: params };
  const response = await fetchApi<{ generateLabelDPD: GenerateLabelResponse }>(
    mutation,
    variables,
  );
  return response.generateLabelDPD;
}

/**
 * Génère les étiquettes Easy
 */
export async function generateLabelEasy(
  params: GenerateLabelEasyParams,
): Promise<GenerateLabelResponse> {
  const mutation = `
    mutation GenerateLabelEasy($input: GenerateLabelEasyInput!) {
      generateLabelEasy(input: $input) {
        success
        error
        etiquettes {
          id_commande_etiquette
          id_commande
          id_commande_name
          id_livraison
          etiquette
          tracking_number
        }
      }
    }
  `;

  const variables = { input: params };
  const response = await fetchApi<{ generateLabelEasy: GenerateLabelResponse }>(
    mutation,
    variables,
  );
  return response.generateLabelEasy;
}

/**
 * Génère les étiquettes UPS
 */
export async function generateLabelUPS(
  params: GenerateLabelUPSParams,
): Promise<GenerateLabelResponse> {
  const mutation = `
    mutation GenerateLabelUPS($input: GenerateLabelUPSInput!) {
      generateLabelUPS(input: $input) {
        success
        error
        etiquettes {
          id_commande_etiquette
          id_commande
          id_commande_name
          id_livraison
          etiquette
          tracking_number
        }
      }
    }
  `;

  const variables = { input: params };
  const response = await fetchApi<{ generateLabelUPS: GenerateLabelResponse }>(
    mutation,
    variables,
  );
  return response.generateLabelUPS;
}
